import { useCallback, useMemo } from 'react';
import {
  createParser,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
  type UrlKeys,
} from 'nuqs';
import { useRecoilValue } from 'recoil';
import { userTraceV2EnabledState } from '../../states';
import {
  OIEntity,
  OIScaleRange,
  TraceLense,
  TraceStrikeBarType,
  PRICE_BOUNDS,
  STRIKE_ZOOM_BOUNDS,
} from '../../types';
import {
  DEFAULT_CANDLE_DURATION_SECONDS,
  DEFAULT_STATS_LOOKBACK_DAYS,
  DEFAULT_TRACE_SYM,
  TraceColorsConfig,
  TraceConfig,
} from '../../types/trace';
import { HEATMAP_DEFAULT_SPX_HIRO_SYM } from '../../config/oi';
import { getQueryDateFormatted } from '../../util/shared/date';

/**
 * Returns the runtime default TraceConfig. Kept out of types/trace.ts so that
 * module doesn't need to import config/ or util/ (which would create a
 * circular dependency via the types barrel).
 */
export const getDefaultTraceConfig = (): TraceConfig => ({
  sym: DEFAULT_TRACE_SYM,
  date: getQueryDateFormatted(true),
  timestamp: undefined,
  lense: TraceLense.GAMMA,
  mktActor: OIEntity.MARKET_MAKER,
  inverted: false,
  filterLinesBasedOnTimeSlider: true,
  strikeBarType: TraceStrikeBarType.GAMMA,
  strikeZoom: STRIKE_ZOOM_BOUNDS[0],
  strikeBarsTrackerEnabled: true,
  strikeBarsHistoryEnabled: true,
  showGexZeroDte: false,
  priceZoom: PRICE_BOUNDS[6],
  candleDuration: DEFAULT_CANDLE_DURATION_SECONDS,
  scaleRange: OIScaleRange.AUTO,
  showKeyLevels: true,
  showColorScale: true,
  showContourLines: true,
  useWhiteMode: false,
  hiroSyms: { SPX: HEATMAP_DEFAULT_SPX_HIRO_SYM },
  statsLookbackDays: DEFAULT_STATS_LOOKBACK_DAYS,
  showPriceLine: false,
});

// Parser key names match TraceConfig field names 1:1 so we don't need
// a hand-written translation layer between the two. The only exceptions
// are `colors` (TraceConfig spreads color fields flat; nuqs bundles them
// into a single JSON blob to keep the URL tidy), and `timestamp` (TraceConfig
// uses `string | undefined`; nuqs uses nullable strings).

// TraceLense is a numeric enum; Object.values() returns reverse-mapped keys
// alongside numeric values, so build an explicit value list and a custom parser.
const TRACE_LENSE_VALUES: readonly TraceLense[] = [
  TraceLense.DELTA_END_DIFF,
  TraceLense.GAMMA,
  TraceLense.GAMMA_DIRECTIONAL,
  TraceLense.GAMMA_COLOR,
  TraceLense.DELTA,
  TraceLense.DELTA_DIRECTIONAL,
  TraceLense.DELTA_CHARM_DIRECTIONAL,
];

function parseNumericEnum<T extends number>(validValues: readonly T[]) {
  const set = new Set<number>(validValues);
  return createParser<T>({
    parse: (raw: string): T | null => {
      const n = Number(raw);
      return !isNaN(n) && Number.isInteger(n) && set.has(n) ? (n as T) : null;
    },
    serialize: (v: T) => String(v),
  });
}

function parseHiroSymsJson(raw: unknown): Record<string, string> {
  if (raw == null || typeof raw !== 'object') {
    return { SPX: HEATMAP_DEFAULT_SPX_HIRO_SYM };
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') {
      out[k] = v;
    }
  }
  return out;
}

function parseColorsJson(raw: unknown): TraceColorsConfig {
  if (raw == null || typeof raw !== 'object') {
    return {};
  }
  return raw as TraceColorsConfig;
}

// Parsers are a module-level constant on purpose. nuqs's `useQueryStates`
// uses parser identity to decide whether to re-parse the URL — passing a
// rebuilt parsers object on every Recoil settle would make config.date
// briefly snap to its default (today's date), which would trigger spurious
// strike-bar re-fetches with the wrong date.
//
// V2-specific defaults (mktActor=CUST, inverted=true when v2 is off) are
// applied as a post-parse adjustment in useTraceConfig instead of in
// parser defaults — keeps PARSERS stable.
const PARSERS = {
  sym: parseAsString.withDefault(DEFAULT_TRACE_SYM),
  // No default here. The "today" fallback is computed in the config mapping
  // (each evaluation, never baked into a parser) so the parser identity is
  // truly fixed.
  //
  // `date` IS in the URL — that lets users share a link to a specific
  // historical date or hit Back to return to one. It is NOT mirrored into
  // the localStorage shadow (see TraceStandaloneWrapper), so a fresh page
  // load with no URL params always defaults to today rather than restoring
  // a stale date from a previous session.
  date: parseAsString,
  timestamp: parseAsString, // nullable; omitted ⇒ latest
  lense: parseNumericEnum(TRACE_LENSE_VALUES).withDefault(TraceLense.GAMMA),
  mktActor: parseAsStringEnum<OIEntity>(Object.values(OIEntity)).withDefault(
    OIEntity.MARKET_MAKER,
  ),
  inverted: parseAsBoolean.withDefault(false),
  filterLinesBasedOnTimeSlider: parseAsBoolean.withDefault(true),
  strikeBarType: parseAsStringEnum<TraceStrikeBarType>(
    Object.values(TraceStrikeBarType),
  ).withDefault(TraceStrikeBarType.GAMMA),
  scaleRange: parseAsStringEnum<OIScaleRange>(
    Object.values(OIScaleRange),
  ).withDefault(OIScaleRange.AUTO),
  priceZoom: parseAsFloat.withDefault(PRICE_BOUNDS[6]),
  strikeZoom: parseAsFloat.withDefault(STRIKE_ZOOM_BOUNDS[0]),
  candleDuration: parseAsInteger.withDefault(DEFAULT_CANDLE_DURATION_SECONDS),
  statsLookbackDays: parseAsInteger.withDefault(DEFAULT_STATS_LOOKBACK_DAYS),
  showKeyLevels: parseAsBoolean.withDefault(true),
  showColorScale: parseAsBoolean.withDefault(true),
  showContourLines: parseAsBoolean.withDefault(true),
  showGexZeroDte: parseAsBoolean.withDefault(false),
  showPriceLine: parseAsBoolean.withDefault(false),
  useWhiteMode: parseAsBoolean.withDefault(false),
  strikeBarsTrackerEnabled: parseAsBoolean.withDefault(true),
  strikeBarsHistoryEnabled: parseAsBoolean.withDefault(true),
  hiroSyms: parseAsJson<Record<string, string>>(parseHiroSymsJson).withDefault({
    SPX: HEATMAP_DEFAULT_SPX_HIRO_SYM,
  }),
  colors: parseAsJson<TraceColorsConfig>(parseColorsJson).withDefault(
    {} as TraceColorsConfig,
  ),
};

type TraceParsers = typeof PARSERS;

const COLOR_FIELDS = new Set<keyof TraceColorsConfig>([
  'posColor',
  'negColor',
  'posStrikeColor',
  'negStrikeColor',
  'putColor',
  'callColor',
  'upCandleFill',
  'downCandleFill',
  'upCandleLineColor',
  'downCandleLineColor',
  'hiroColor',
  'contourLineColor',
  'customColorsPerLense',
]);

export function useTraceConfig(
  urlKeyPrefix = 'tr',
): [TraceConfig, (patch: Partial<TraceConfig>) => void] {
  const v2Enabled = useRecoilValue(userTraceV2EnabledState);

  // urlKeys depends on the prefix only — PARSERS is module-stable.
  const urlKeys = useMemo<UrlKeys<TraceParsers>>(
    () =>
      Object.fromEntries(
        Object.keys(PARSERS).map((k) => [k, `${urlKeyPrefix}_${k}`]),
      ) as UrlKeys<TraceParsers>,
    [urlKeyPrefix],
  );

  const [params, setParams] = useQueryStates(PARSERS, { urlKeys });

  // Parser keys match TraceConfig keys. Special cases:
  //   - `colors`: a JSON blob in nuqs; spread into top-level fields.
  //   - `timestamp`: nullable in nuqs, optional in TraceConfig.
  //   - `date`: today-fallback applied here (parser stays static).
  //   - `mktActor`/`inverted`: v2-rollback defaults applied here so PARSERS
  //     stays a stable module-level constant.
  const config = useMemo<TraceConfig>(() => {
    const { colors, timestamp, date, mktActor, inverted, ...rest } = params;
    return {
      ...rest,
      date: date ?? getQueryDateFormatted(true),
      timestamp: timestamp ?? undefined,
      mktActor: v2Enabled ? mktActor : OIEntity.CUST,
      inverted: v2Enabled ? inverted : true,
      ...(colors ?? {}),
    };
  }, [params, v2Enabled]);

  const updateConfig = useCallback(
    (patch: Partial<TraceConfig>) => {
      const urlPatch: Partial<typeof params> = {};
      const colorPatch: Partial<TraceColorsConfig> = {};

      for (const key of Object.keys(patch) as (keyof TraceConfig)[]) {
        if (key === 'timestamp') {
          // nuqs wants null to clear; TraceConfig uses undefined.
          urlPatch.timestamp = patch.timestamp ?? null;
        } else if (COLOR_FIELDS.has(key as keyof TraceColorsConfig)) {
          // Preserve undefined so callers can explicitly reset a color.
          Object.assign(colorPatch, { [key]: patch[key] });
        } else if (patch[key] != null) {
          Object.assign(urlPatch, { [key]: patch[key] });
        }
      }

      if (Object.keys(colorPatch).length > 0) {
        urlPatch.colors = { ...(params.colors ?? {}), ...colorPatch };
      }

      if (Object.keys(urlPatch).length > 0) {
        void setParams(urlPatch);
      }
    },
    [params, setParams],
  );

  return [config, updateConfig];
}
